import { useRouter } from 'next/router';
import Link from 'next/link';
import { ArrowLeft, Mail, Globe, Shield, FileText, MapPin } from 'lucide-react';

const sections = [
  {
    title: '1. About Gyansathi',
    body: 'Gyansathi is an educational platform providing academic guidance, skill development programs, online learning resources, career counseling, certification courses, and student support services.',
  },
  {
    title: '2. Information We Collect',
    body: 'We may collect personal information including your name, email address, phone number, educational details, address, date of birth, and payment-related information where applicable.',
  },
  {
    title: '3. Automatically Collected Information',
    body: 'We may automatically collect device information, browser details, IP address, cookies, and website usage analytics to improve user experience.',
  },
  {
    title: '4. How We Use Your Information',
    body: 'Your information is used for student registration, educational services, communication, customer support, issuing certificates, improving services, marketing communications (with consent), and legal compliance.',
  },
  {
    title: '5. To Communicate with You',
    body: 'We may use your information to respond to your inquiries, provide customer service support, send you important information about the services, and send you marketing communications (with your consent) via different channels, including but not limited to SMS, RCS, Email, WhatsApp, and Voice.',
  },
  {
    title: '6. Sharing of Information',
    body: 'Gyansathi does not sell personal data. Information may be shared with educational partners, payment gateways, technology providers, or government authorities where legally required.',
  },
  {
    title: '7. Cookies Policy',
    body: 'Our website may use cookies and tracking technologies to improve user experience and website performance.',
  },
  {
    title: '8. Data Security',
    body: 'We implement appropriate technical and organizational security measures to protect your personal information from unauthorized access, misuse, or disclosure.',
  },
  {
    title: '9. Data Retention',
    body: 'We retain user information only for as long as necessary to provide services, maintain records, comply with legal obligations, and resolve disputes.',
  },
  {
    title: '10. Your Rights',
    body: 'Users have the right to access, update, correct, or request deletion of their personal information, subject to applicable laws.',
  },
  {
    title: '11. Third-Party Links',
    body: 'Our website may contain links to third-party websites. Gyansathi is not responsible for the privacy practices of external websites.',
  },
  {
    title: "12. Children's Privacy",
    body: 'For users under the age of 18, parental or guardian consent may be required where applicable.',
  },
  {
    title: '13. Changes to This Privacy Policy',
    body: 'Gyansathi reserves the right to update this Privacy Policy at any time. Updated versions will be published on the website.',
  },
  {
    title: '14. Disclaimer',
    body: 'All educational content and guidance are provided for informational purposes only. Gyansathi does not guarantee specific academic or employment outcomes.',
  },
  {
    title: '15. Legal Compliance',
    body: 'This Privacy Policy is governed by the applicable laws of India, including the Information Technology Act, 2000.',
  },
];

const PrivacyPolicyPage = () => {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-pastel-cream">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <button
              onClick={() => router.push('/')}
              className="flex items-center space-x-3"
            >
              <img
                src="/assets/logo-icon.png"
                alt="Gyan Sathi Logo"
                className="h-12 w-auto"
              />
              <div className="text-left">
                <span className="text-2xl font-bold text-pastel-navy">GYAN SATHI</span>
                <p className="text-xs text-gray-600 -mt-0.5">Smart Learning Platform</p>
              </div>
            </button>
            <button
              onClick={() => router.push('/')}
              className="hidden sm:inline-flex items-center space-x-2 px-5 py-2.5 bg-white border-2 border-pastel-blue-dark text-pastel-navy rounded-full hover:bg-pastel-blue/10 transition-all duration-300 text-sm font-semibold shadow-soft"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-pastel-rose/30 rounded-full blur-3xl" />
          <div className="absolute top-20 right-10 w-96 h-96 bg-pastel-blue/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-pastel-lavender/30 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm border-2 border-pastel-yellow rounded-full px-5 py-2.5 shadow-soft mb-6">
            <Shield className="w-4 h-4 text-pastel-peach-dark" />
            <span className="text-sm font-semibold text-pastel-navy">Your Privacy Matters</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-pastel-navy mb-4 leading-tight">
            Privacy Policy
          </h1>
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="h-1.5 w-20 bg-pastel-rose rounded-full" />
            <div className="h-1.5 w-12 bg-pastel-yellow rounded-full" />
            <div className="h-1.5 w-8 bg-pastel-mint rounded-full" />
          </div>
          <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
            Welcome to Gyansathi. Your privacy is important to us. This Privacy Policy explains how we
            collect, use, store, and protect your information when you use our website, mobile application,
            educational services, and related platforms.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-soft-xl border border-gray-100 p-8 sm:p-12 space-y-8">
            {sections.map((section, index) => (
              <div key={index} className="group">
                <div className="flex items-start space-x-4">
                  <div className="hidden sm:flex w-10 h-10 bg-pastel-blue/30 rounded-xl items-center justify-center flex-shrink-0 mt-1">
                    <FileText className="w-5 h-5 text-pastel-blue-dark" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl sm:text-2xl font-bold text-pastel-navy mb-3">
                      {section.title}
                    </h2>
                    <p className="text-gray-700 leading-relaxed">{section.body}</p>
                  </div>
                </div>
                {index < sections.length - 1 && (
                  <div className="mt-8 border-b border-gray-100" />
                )}
              </div>
            ))}

            {/* Contact Card */}
            <div className="mt-10 bg-pastel-cream rounded-2xl p-6 sm:p-8 border-2 border-pastel-blue/30">
              <h2 className="text-2xl font-bold text-pastel-navy mb-4">16. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about this Privacy Policy or how your information is handled,
                please reach out to us:
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-soft">
                    <Mail className="w-5 h-5 text-pastel-blue-dark" />
                  </div>
                  <a
                    href="mailto:info@gyansathi.com"
                    className="text-pastel-navy font-semibold hover:text-pastel-blue-dark transition-colors"
                  >
                    info@gyansathi.com
                  </a>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-soft flex-shrink-0">
                    <MapPin className="w-5 h-5 text-pastel-blue-dark" />
                  </div>
                  <a
                    href="https://www.google.com/maps?q=28.946156,77.754158"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pastel-navy font-semibold hover:text-pastel-blue-dark transition-colors pt-2"
                  >
                    SF 66A, Somdutt City, A Pocket, Meerut
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-soft">
                    <Globe className="w-5 h-5 text-pastel-blue-dark" />
                  </div>
                  <Link
                    href="/contact-us"
                    className="text-pastel-navy font-semibold hover:text-pastel-blue-dark transition-colors"
                  >
                    Visit our Contact Us page
                  </Link>
                </div>
              </div>
              <p className="mt-6 text-sm text-gray-600">
                Also see our{' '}
                <Link
                  href="/terms-and-conditions"
                  className="text-pastel-blue-dark font-semibold hover:underline"
                >
                  Terms &amp; Conditions
                </Link>{' '}
                for the rules that govern the use of our platform.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-pastel-navy text-white py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img
              src="/assets/logo-icon.png"
              alt="Gyan Sathi Logo"
              className="h-10 w-auto"
            />
            <span className="text-xl font-bold">GYAN SATHI</span>
          </div>
          <p className="text-white/60 text-sm">
            &copy; 2026 Gyansathi. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
